import { useMemo } from 'react';
import { LabelOrigin, LabelType, TimeSignal } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useFormattedDuration } from 'modules/notes/common';
import { useMeContext } from 'modules/me';
import { formatTimeFromIsoString } from 'utils/date-time';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';

export const useLabelInfo = ({
  note
}: {
  note: TimeSignal;
}): {
  timesheetLabels: TimesheetLabel[];
  customLabels: TimesheetLabel[];
  formattedDuration: string;
  formattedTime: {
    start: string;
    end: string;
  };
} => {
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const me = useMeContext();

  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: note.duration ?? 0
  });

  const timesheetLabels = useMemo(
    () =>
      note.labelValues
        ?.filter((l) => {
          if (l.label?.type === LabelType.Comment && !l.value) {
            return false;
          }
          return l.label?.origin !== LabelOrigin.User;
        })
        .map((l) => {
          const retVal: TimesheetLabel = {
            ...l.label,
            value: l.value
          };
          if (isSmartSuggestionEnabled) {
            retVal.valueSource = l.valueSource;
          }
          return retVal;
        }),
    [isSmartSuggestionEnabled, note.labelValues]
  );

  const customLabels = useMemo(
    () =>
      note.labelValues
        ?.filter((l) => l.label?.origin === LabelOrigin.User && l.value)
        .map((l) => {
          const retVal: TimesheetLabel = {
            ...l.label,
            value: l.value
          };
          if (isSmartSuggestionEnabled) {
            retVal.valueSource = l.valueSource;
          }

          return retVal;
        }),
    [isSmartSuggestionEnabled, note.labelValues]
  );

  const formattedTime = useMemo(
    () => ({
      start: formatTimeFromIsoString(note.startTime, me),
      end: formatTimeFromIsoString(note.endTime, me)
    }),
    [me, note.endTime, note.startTime]
  );

  return { timesheetLabels, customLabels, formattedDuration, formattedTime };
};
